import { type FunctionComponent, useLayoutEffect, useState } from 'react';

import { useParams } from '@tanstack/react-router';

import type { JobsJobElasticSchemaWithId } from '@iconik-io/iconik-api/src/services/jobs';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';

import { historyPush } from '../../../ducks/history/actions';
import { getJob } from '../../../ducks/pages/admin_job/thunks';
import { selectAssetOriginalFormat } from '../../../ducks/pages/asset/selectors';
import { denormalizeSearchItem } from '../../../ducks/search/normalizers';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../store/react_redux_typed';
import ControlsModal from '../../../ui/controls_modal/controls_modal';
import LoadingBar from '../../../ui/loading_bar/loading_bar';
import Job from '../../job/job';
import NoContent from '../../no_content/no_content';
import WebSocketJobs from '../../websocket_jobs/websocket_jobs';
import withContextItems from '../context_items_hoc';
import type {
  ContextItemsHocProps,
  ModalComponentProps,
} from '../modals_manager/types';

type Props = ContextItemsHocProps<JobsJobElasticSchemaWithId> &
  ModalComponentProps;

const JobModal: FunctionComponent<Props> = (props) => {
  const { item } = props;
  const dispatch = useAppDispatch();
  const { versionId: routeVersionId } = useParams({ strict: false });
  const [loading, setLoading] = useState(false);

  const job = useAppSelector((state) =>
    item ? state.entities.jobs[item.id] : undefined,
  );
  const jobAssetId =
    job?.object_type === OBJECT_TYPE_ASSETS ? job.object_id : undefined;
  const searchItem = useAppSelector((state) =>
    jobAssetId ? denormalizeSearchItem(state.entities, jobAssetId) : undefined,
  );
  const originalFormat = useAppSelector((state) =>
    jobAssetId
      ? selectAssetOriginalFormat(state, jobAssetId, routeVersionId)
      : undefined,
  );
  const dateTimeFormat = useAppSelector(
    (state) => state.appReducer.dateTimeFormat,
  );

  useLayoutEffect(() => {
    setLoading(true);
    if (item) {
      dispatch(getJob(item.id)).finally(() => setLoading(false));
    }
  }, [item]);

  let content;
  if (loading) {
    content = <LoadingBar />;
  } else if (!job) {
    content = <NoContent title="Job not found" />;
  } else {
    content = (
      <>
        <WebSocketJobs job={job} />
        <Job
          job={job}
          searchItem={searchItem}
          originalFormat={originalFormat}
          dateTimeFormat={dateTimeFormat}
          onJobOpenClick={() => {
            dispatch(historyPush(`/admin/jobs/${job.id}`));
          }}
        />
      </>
    );
  }

  return (
    <ControlsModal
      headerText={job?.title ?? 'Job'}
      isOpen={props.isOpen}
      onCancel={props.onCancel}
      cancelButtonText="Close"
    >
      {content}
    </ControlsModal>
  );
};

export default withContextItems(JobModal);
